/**
 * CRM OX 퀴즈 미리보기 전용 화면.
 *
 * 인증·하트비트·태그 API가 없는 독립 진입점이며, 부모 CRM iframe이 postMessage로 전달한
 * 저장 전 설정만 실제 OxQuizMissionFlow에 넣어 실행한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import OxQuizMissionFlow from '../components/quest/OxQuizMissionFlow';
import './OxQuizPreviewPage.css';

export const OX_PREVIEW_LOAD_MESSAGE = 'zlzl:ox-quiz-preview:load';
export const OX_PREVIEW_READY_MESSAGE = 'zlzl:ox-quiz-preview:ready';
export const OX_PREVIEW_COMPLETE_MESSAGE = 'zlzl:ox-quiz-preview:complete';
export const OX_PREVIEW_VIDEO_ABORT_MESSAGE = 'zlzl:ox-quiz-preview:video-abort';

/** 외부 메시지를 플레이어에 넣기 전에 최소 계약만 검증한다. */
export function normalizeOxPreviewMessage(data) {
  if (!data || data.type !== OX_PREVIEW_LOAD_MESSAGE) return null;
  const preview = data.preview;
  if (!preview || preview.spot?.mission_type !== 'ox_quiz') return null;
  if (!preview.config || !Array.isArray(preview.config.questions)
      || preview.config.questions.length === 0) return null;

  return {
    runId: String(data.run_id ?? ''),
    spot: preview.spot,
    config: preview.config,
    spotName: typeof preview.spot_name === 'string' ? preview.spot_name : '',
    memberNickname: typeof preview.member_nickname === 'string'
      ? preview.member_nickname
      : '미리보기',
  };
}

export default function OxQuizPreviewPage() {
  const [payload, setPayload] = useState(null);
  const [started, setStarted] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState('');
  const parentOriginRef = useRef('*');

  const sendParent = useCallback((message) => {
    if (window.parent === window) return;
    window.parent.postMessage(message, parentOriginRef.current || '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      // iframe 부모 외의 창이 임의로 실행 데이터를 밀어 넣지 못하게 한다.
      if (event.source !== window.parent) return;
      const normalized = normalizeOxPreviewMessage(event.data);
      if (!normalized) {
        if (event.data?.type === OX_PREVIEW_LOAD_MESSAGE) {
          setInvalidMessage('미리보기 데이터를 불러올 수 없습니다.');
        }
        return;
      }
      parentOriginRef.current = event.origin || '*';
      setInvalidMessage('');
      setPayload(normalized);
      setStarted(false);
    };

    window.addEventListener('message', handleMessage);
    sendParent({ type: OX_PREVIEW_READY_MESSAGE });
    return () => window.removeEventListener('message', handleMessage);
  }, [sendParent]);

  if (invalidMessage) {
    return (
      <main className="ox-preview-page ox-preview-state" role="alert">
        <strong>{invalidMessage}</strong>
        <span>CRM에서 미리보기를 다시 실행해 주세요.</span>
      </main>
    );
  }

  if (!payload) {
    return (
      <main className="ox-preview-page ox-preview-state" aria-live="polite">
        <strong>OX 퀴즈 미리보기를 준비하고 있습니다.</strong>
      </main>
    );
  }

  // 소리가 있는 교차 출처 iframe 영상은 브라우저가 autoplay를 막을 수 있다. iframe 안에서
  // 직접 받은 사용자 클릭으로 흐름을 마운트해야 인트로부터 실제 재생되는 조건이 만들어진다.
  if (!started) {
    return (
      <main className="ox-preview-page ox-preview-launch">
        <button type="button" onClick={() => setStarted(true)}>
          <span aria-hidden="true">▶</span>
          미리보기 시작
        </button>
        <p>인트로 영상부터 태블릿 실행 순서대로 재생합니다.</p>
      </main>
    );
  }

  return (
    <main className="ox-preview-page" data-testid="ox-preview-page">
      <div className="ox-preview-stage" key={payload.runId}>
        <OxQuizMissionFlow
          spot={payload.spot}
          config={payload.config}
          spotName={payload.spotName}
          memberNickname={payload.memberNickname}
          onComplete={(result) => sendParent({
            type: OX_PREVIEW_COMPLETE_MESSAGE,
            result,
          })}
          onCancel={() => {}}
          onVideoAbort={(reason) => sendParent({
            type: OX_PREVIEW_VIDEO_ABORT_MESSAGE,
            reason,
          })}
        />
      </div>
    </main>
  );
}
